import { Button, TableCell, Typography } from '@mui/material';
import { Add, ArrowDown2, DocumentUpload, Edit2 } from 'iconsax-react';
import { Fragment, type MouseEvent, type MouseEventHandler, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { SlabTheme } from '../../../theme';
import { ExpandMore } from '../../ExpandMore/ExpandMore';
import type { Column } from '../TableDataModel';

export const FormatLinkCell = ({ label, link }: { label: string; link: string }): ReactElement => (
  <Link to={link}>{label}</Link>
);

const handleEmailCheck = (event: MouseEvent, email: string | null): void => {
  event.stopPropagation();
  window.location.href = `mailto:${email}`;
  setTimeout(() => {
    if (document.hasFocus()) {
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email as string)}`,
        '_blank',
        'noopener,noreferrer',
      );
    }
  }, 500);
};

export const FormatEmailLinkCell = ({
  label,
  link,
  email,
}: {
  label: string;
  link: string;
  email: string | null;
}): ReactElement => (
  <Link to={link} onClick={(e) => handleEmailCheck(e, email)} role='button'>
    {label}
  </Link>
);

export const FormatNotSetCell = (value: string | null | undefined): ReactElement => {
  if (value === undefined || value === null || value === '') {
    return <Typography color={SlabTheme.palette.SlabGray['400']}>[not set]</Typography>;
  }
  return <Typography color={SlabTheme.palette.SlabGray['800']}>{value}</Typography>;
};

/**
 * FormatErrorCell accepts a string value and if it is non-empty, will be displayed
 * in red like an error value.
 *
 * If the value provided is empty, `[no error]` in light text will be displayed.
 */
export const FormatErrorCell = (value: string | null | undefined): ReactElement => {
  if (value === undefined || value === null || value === '') {
    return <Typography color={SlabTheme.palette.SlabGray['400']}>[no error]</Typography>;
  }
  return <Typography color={SlabTheme.palette.error.main}>{value}</Typography>;
};

export const FormatButtonCell = ({
  label,
  onClick,
  icon,
}: {
  label: string | null;
  onClick: MouseEventHandler<HTMLButtonElement>;
  icon?: 'add' | 'edit' | 'send';
}): ReactElement => {
  const iconElement = ((): ReactElement | null => {
    const hasLabel = label !== '' && label !== null;
    const iconStyle = { paddingRight: hasLabel ? '0.5rem' : '0' };
    switch (icon) {
      case 'add':
        return <Add style={iconStyle} />;
      case 'edit':
        return <Edit2 style={iconStyle} />;
      case 'send':
        return <DocumentUpload style={iconStyle} />;
      default:
        return null;
    }
  })();
  return (
    <Button sx={{ minWidth: 'auto' }} onClick={onClick}>
      <>
        {iconElement}
        {label}
      </>
    </Button>
  );
};

type TableRowProps<TRow extends { id: string }> = {
  renderedColumns: ReadonlySet<Column<TRow>>;
  row: TRow;
  labelID: string;
  isExpanded?: boolean;
  isExpandable?: boolean;
  onIsExpandedChange?: (id: string, newExpanded: boolean) => void;
};

/**
 * SlabTableRowCells takes a row & the according columns to display and constructs
 * the appropriate looking TableCells to be displayed in a TableRow.
 */
export const SlabTableRowCells = <TRow extends { id: string }>({
  renderedColumns,
  row,
  labelID,
  isExpanded,
  isExpandable,
  onIsExpandedChange,
}: TableRowProps<TRow>): ReactElement => (
  <>
    {[...renderedColumns].map((column, index) => (
      <Fragment key={column.id}>
        {/* Spacing for SeparatorIcon in THead */}
        {index > 0 && <TableCell padding='none' />}
        <TableCell id={`${labelID}-${column.id}`} sx={{ padding: '0.5rem' }}>
          {isExpandable && index === 0 ? (
            <ExpandMore
              expand={isExpanded ?? false}
              onClick={() => onIsExpandedChange?.(row.id, !isExpanded)}
            >
              <ArrowDown2 />
            </ExpandMore>
          ) : null}
          {column.toWeb(row)}
        </TableCell>
      </Fragment>
    ))}
  </>
);
